import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  SimpleGrid,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Select,
  Heading,
  Divider,
  Radio,
  RadioGroup,
  IconButton,
  Tooltip,
  NumberInput,
  NumberInputField,
  useToast,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import { PURPOSES, PURPOSE_LABELS, type Purpose, type CreateLinkInput, type StringMap } from '../../types/link';
import { branchByPurpose, flattenZodError, LINK_NAME_MAX } from '../../lib/validation';
import { toDatetimeLocalValue } from '../../lib/format';
import { generateSlug, buildPreviewShortUrl } from '../../lib/slug';
import { syncUtmCampaign } from '../../lib/utmSync';
import { useCreateLinkMutation, useGetAppProfileQuery } from '../../store/linksApi';
import { KeyValueEditor } from './KeyValueEditor';
import DeepLinkPicker from '../../../../../components/DeepLinkPicker/DeepLinkPicker';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
}

interface FormState {
  linkName: string;
  slug: string;
  kind: 'standard' | 'referral';
  referralCode: string;
  purpose: Purpose;
  // Website
  websiteUrl: string;
  // App Download
  androidAppStoreUrl: string;
  iosAppStoreUrl: string;
  ipadAppStoreUrl: string;
  huaweiAppGalleryUrl: string;
  webFallbackUrl: string;
  // Deep Link
  androidDeepLink: string;
  iosDeepLink: string;
  // WhatsApp click-to-chat
  whatsappNumber: string;
  whatsappMessage: string;
  // Mobile-app / social-breakout config (app_download + deep_link)
  socialReady: boolean;
  androidPackageName: string;
  appScheme: string;
  // Deep Link uses androidAppStoreUrl + iosAppStoreUrl above as fallbacks too.
  // Tracking / lifecycle
  campaignName: string;
  campaignId: string;
  expiryMode: 'never' | 'on';
  expiresAt: string;
  attributionWindowHours: number;
}

function makeEmpty(): FormState {
  return {
    linkName: '',
    slug: generateSlug(),
    kind: 'standard',
    referralCode: '',
    purpose: 'website',
    websiteUrl: '',
    androidAppStoreUrl: '',
    iosAppStoreUrl: '',
    ipadAppStoreUrl: '',
    huaweiAppGalleryUrl: '',
    webFallbackUrl: '',
    androidDeepLink: '',
    iosDeepLink: '',
    whatsappNumber: '',
    whatsappMessage: '',
    socialReady: false,
    androidPackageName: '',
    appScheme: '',
    campaignName: '',
    campaignId: '',
    expiryMode: 'never',
    expiresAt: '',
    attributionWindowHours: 168,
  };
}

/** Auto-generated referral code (becomes the referrer's id) for a referral link. */
const generateReferralCode = () => generateSlug().toUpperCase();

/** Build the Zod input payload for the current purpose branch. */
function buildPurposePayload(form: FormState) {
  switch (form.purpose) {
    case 'website':
      return { purpose: form.purpose as 'website', websiteUrl: form.websiteUrl };
    case 'app_download':
      return {
        purpose: form.purpose as 'app_download',
        androidAppStoreUrl: form.androidAppStoreUrl,
        iosAppStoreUrl: form.iosAppStoreUrl,
        ipadAppStoreUrl: form.ipadAppStoreUrl,
        huaweiAppGalleryUrl: form.huaweiAppGalleryUrl,
        webFallbackUrl: form.webFallbackUrl,
        androidPackageName: form.socialReady ? form.androidPackageName : '',
        appScheme: form.socialReady ? form.appScheme : '',
      };
    case 'deep_link':
      return {
        purpose: form.purpose as 'deep_link',
        androidDeepLink: form.androidDeepLink,
        iosDeepLink: form.iosDeepLink,
        androidAppStoreUrl: form.androidAppStoreUrl,
        iosAppStoreUrl: form.iosAppStoreUrl,
        webFallbackUrl: form.webFallbackUrl, // SM_006
        androidPackageName: form.socialReady ? form.androidPackageName : '',
        appScheme: form.socialReady ? form.appScheme : '',
      };
    case 'whatsapp':
      return {
        purpose: form.purpose as 'whatsapp',
        whatsappNumber: form.whatsappNumber,
        whatsappMessage: form.whatsappMessage,
      };
  }
}

/** Resolve the primary `destination` the backend stores. */
function resolveDestination(form: FormState): string {
  switch (form.purpose) {
    case 'website':
      return form.websiteUrl;
    case 'app_download':
      return form.webFallbackUrl;
    case 'deep_link':
      // SM_006: prefer the web fallback, then a store URL, then the deep link itself.
      return form.webFallbackUrl || form.androidAppStoreUrl || form.iosAppStoreUrl || form.androidDeepLink || form.iosDeepLink;
    case 'whatsapp': {
      const digits = form.whatsappNumber.replace(/\D/g, '');
      if (!digits) return '';
      const base = `https://wa.me/${digits}`;
      return form.whatsappMessage ? `${base}?text=${encodeURIComponent(form.whatsappMessage)}` : base;
    }
  }
}

// SL_002: the user's timezone + UTC offset, shown next to the expiry picker so
// it is unambiguous which zone the wall-clock time is entered in.
function localTzLabel(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offMin = -new Date().getTimezoneOffset();
    const sign = offMin >= 0 ? '+' : '-';
    const hh = String(Math.floor(Math.abs(offMin) / 60)).padStart(2, '0');
    const mm = String(Math.abs(offMin) % 60).padStart(2, '0');
    return `${tz} (UTC${sign}${hh}:${mm})`;
  } catch {
    return 'your local timezone';
  }
}

export const CreateLinkModal: React.FC<Props> = ({ isOpen, onClose, onCreated }) => {
  const [form, setForm] = useState<FormState>(() => makeEmpty());
  const [deepLinkParams, setDeepLinkParams] = useState<StringMap>({});
  const [utmParams, setUtmParams] = useState<StringMap>({});
  // SL_004: completeness of the UTM / deep-link parameter editors — block submit while incomplete.
  const [utmValid, setUtmValid] = useState(true);
  const [paramsValid, setParamsValid] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // SM_011: the tenant's ACTIVE custom domain (if any), so the preview matches the
  // short_url the API returns instead of hardcoding link.xnotify.com.
  const [activeDomain, setActiveDomain] = useState<string | null>(null);
  const [createLink, { isLoading }] = useCreateLinkMutation();
  const { data: appProfile } = useGetAppProfileQuery(undefined, { skip: !isOpen });
  const toast = useToast();

  // Track the last sync source so the user can override the auto-filled
  // utm_campaign / utm_reference without us clobbering their edit on the
  // next keystroke. If the row's value still equals the previously synced
  // sibling-field value, the user hasn't customized — safe to update.
  const prevCampaignId = useRef<string>('');

  // Re-seed a fresh slug + state every time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setForm(makeEmpty());
      setDeepLinkParams({});
      setUtmParams({});
      setUtmValid(true);
      setParamsValid(true);
      setErrors({});
      prevCampaignId.current = '';
    }
  }, [isOpen]);

  // SM_011: load the tenant's custom-domain status (same endpoint as the Custom
  // Domain page) so the Short-link preview reflects the mapped domain when active.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const baseUrl = process.env.REACT_APP_SMARTLINKS_API_URL || 'http://localhost:4001';
    fetch(`${baseUrl}/domains`, { headers: { 'x-access-token': localStorage.getItem('customer-portal') || '' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((row) => { if (!cancelled) setActiveDomain(row && row.status === 'active' ? row.hostname : null); })
      .catch(() => { if (!cancelled) setActiveDomain(null); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Pre-fill store URLs from the tenant's App Profile when one exists, so the
  // marketer doesn't re-enter them (they stay editable as per-link overrides).
  // `f.x || …` never clobbers a value the user has already typed.
  useEffect(() => {
    if (!isOpen || !appProfile) return;
    setForm((f) => ({
      ...f,
      androidAppStoreUrl: f.androidAppStoreUrl || appProfile.androidAppStoreUrl || '',
      iosAppStoreUrl: f.iosAppStoreUrl || appProfile.iosAppStoreUrl || '',
      ipadAppStoreUrl: f.ipadAppStoreUrl || appProfile.ipadAppStoreUrl || '',
      huaweiAppGalleryUrl: f.huaweiAppGalleryUrl || appProfile.huaweiAppGalleryUrl || '',
      webFallbackUrl: f.webFallbackUrl || appProfile.webFallbackUrl || '',
    }));
  }, [isOpen, appProfile]);

  // Auto-fill utm_campaign from Campaign ID. Stays editable, and SM_004: clearing
  // the Campaign ID also drops the auto-synced utm_campaign (see syncUtmCampaign).
  useEffect(() => {
    const prev = prevCampaignId.current;
    prevCampaignId.current = form.campaignId.trim();
    setUtmParams((p) => syncUtmCampaign(p, prev, form.campaignId));
  }, [form.campaignId]);


  // Referral links attribute app installs, so they can't be a plain website, and
  // social sharing is the norm — on switch to Referral, default to an app purpose
  // + Social-ready. Runs only when `kind` flips, so it never fights later edits.
  useEffect(() => {
    if (form.kind === 'referral') {
      setForm((f) => ({
        ...f,
        purpose: f.purpose === 'website' || f.purpose === 'whatsapp' ? 'app_download' : f.purpose,
        referralCode: f.referralCode || generateReferralCode(),
      }));
    } else {
      // Back to standard — drop the referral-only utm tag.
      setUtmParams((p) => {
        if (!('referrer_code' in p)) return p;
        const { referrer_code, ...rest } = p;
        return rest;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.kind]);

  // Keep the utm `referrer_code` tag in sync with the referral code.
  useEffect(() => {
    if (form.kind !== 'referral' || !form.referralCode) return;
    setUtmParams((p) => ({ ...p, referrer_code: form.referralCode }));
  }, [form.referralCode, form.kind]);

  // Referral attribution is install-based — hide the Website purpose for referral links.
  const purposeOptions = useMemo(
    () => (form.kind === 'referral' ? PURPOSES.filter((p) => p !== 'website' && p !== 'whatsapp') : PURPOSES),
    [form.kind],
  );

  // Tenant slug = first DNS label of the Customer Portal's own hostname.
  // Mirrors backend src/lib/master-db.js slugOf() which derives the tenant
  // slug from tbl_customer.domain_name's first dot-separated label. So if
  // the operator is on matw.xnotify.com → "matw"; on localhost → "localhost".
  const tenantSlug = useMemo(
    () => (typeof window !== 'undefined' ? window.location.hostname.split('.')[0] : 'tenant'),
    [],
  );

  // SM_011: preview that matches the short_url the API returns (custom domain when active).
  const previewUrl = buildPreviewShortUrl(form.slug, tenantSlug, activeDomain);

  const set = <K extends keyof FormState>(key: K, val: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const regenerateSlug = () => set('slug', generateSlug());

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    const payload = {
      linkName: form.linkName,
      slug: form.slug,
      campaignId: form.campaignId,
      campaignName: form.campaignName,
      expiresAt: form.expiryMode === 'never' ? undefined : form.expiresAt,
      attributionWindowHours: form.attributionWindowHours,
      ...buildPurposePayload(form),
    };

    const parsed = branchByPurpose[form.purpose].safeParse(payload);
    if (!parsed.success) {
      setErrors(flattenZodError(parsed.error));
      return;
    }
    // SL_004: block saving while any UTM / deep-link parameter row is incomplete
    // (a key without a value, or a value without a key).
    if (!utmValid || (form.purpose === 'deep_link' && !paramsValid)) {
      setErrors({ _root: 'Complete or remove incomplete parameter rows — each needs both a key and a value.' });
      return;
    }
    setErrors({});

    const destination = resolveDestination(form);
    if (!destination && form.purpose === 'website') {
      setErrors({ _root: 'Enter a website URL.' });
      return;
    }
    // App-download / deep-link links must still resolve to *something* the SDK can
    // open (a fallback URL or at least one store/app URL). Without this a link can
    // be minted with an empty destination and redirect nowhere.
    if (!destination && (form.purpose === 'app_download' || form.purpose === 'deep_link')) {
      setErrors({ _root: 'Add a fallback URL (or at least one app/store URL) so the link always resolves.' });
      return;
    }
    // "Expires on" with no date silently minted a never-expiring link — require the date.
    if (form.expiryMode === 'on' && !form.expiresAt) {
      setErrors({ expiresAt: 'Pick an expiry date, or switch to "Never".' });
      return;
    }

    const input: CreateLinkInput = {
      linkName: parsed.data.linkName,
      slug: parsed.data.slug,
      kind: form.kind,
      referrerId: form.kind === 'referral' ? form.referralCode : undefined,
      purpose: parsed.data.purpose,
      destination: destination || '',
      campaignId: parsed.data.campaignId,
      campaignName: parsed.data.campaignName,
      attributionWindowHours: parsed.data.attributionWindowHours,
      // SL_002: the <input type="datetime-local"> value is a naive wall-clock
      // string (no timezone). Convert it to an unambiguous UTC ISO-8601 instant
      // in the user's own timezone before sending — the backend requires an
      // offset and interprets it exactly (no server-timezone guessing).
      expiresAt:
        form.expiryMode === 'never' || !parsed.data.expiresAt
          ? null
          : new Date(parsed.data.expiresAt).toISOString(),
      deepLinkParameters: Object.keys(deepLinkParams).length ? deepLinkParams : undefined,
      utmParameters: Object.keys(utmParams).length ? utmParams : undefined,
      ...(parsed.data.purpose === 'website' && { websiteUrl: parsed.data.websiteUrl }),
      ...(parsed.data.purpose === 'app_download' && {
        androidAppStoreUrl: parsed.data.androidAppStoreUrl,
        iosAppStoreUrl: parsed.data.iosAppStoreUrl,
        ipadAppStoreUrl: parsed.data.ipadAppStoreUrl,
        huaweiAppGalleryUrl: parsed.data.huaweiAppGalleryUrl,
        webFallbackUrl: parsed.data.webFallbackUrl,
        androidPackageName: parsed.data.androidPackageName,
        appScheme: parsed.data.appScheme,
      }),
      ...(parsed.data.purpose === 'deep_link' && {
        androidDeepLink: parsed.data.androidDeepLink,
        iosDeepLink: parsed.data.iosDeepLink,
        androidAppStoreUrl: parsed.data.androidAppStoreUrl,
        iosAppStoreUrl: parsed.data.iosAppStoreUrl,
        webFallbackUrl: parsed.data.webFallbackUrl, // SL_006: was omitted → never saved
        androidPackageName: parsed.data.androidPackageName,
        appScheme: parsed.data.appScheme,
      }),
      ...(parsed.data.purpose === 'whatsapp' && {
        whatsappNumber: parsed.data.whatsappNumber,
        whatsappMessage: parsed.data.whatsappMessage,
      }),
    };

    try {
      const created = await createLink(input).unwrap();
      toast({
        title: 'Short link created',
        description: created.shortUrl,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onClose();
      onCreated?.(created.id);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Failed to create link';
      toast({ title: 'Could not create link', description: msg, status: 'error', isClosable: true });
    }
  };

  const field = (key: string) => errors[key];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottomWidth="1px">Create Smart Link</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch" py={2}>
            {/* Link type — Standard vs Referral */}
            <FormControl>
              <FormLabel>Link type</FormLabel>
              <HStack spacing={1} bg="gray.100" rounded="md" p={1} w="fit-content">
                {(['standard', 'referral'] as const).map((k) => (
                  <Button
                    key={k}
                    size="sm"
                    variant="ghost"
                    fontWeight="600"
                    color={form.kind === k ? 'var(--brand-primary)' : 'gray.600'}
                    bg={form.kind === k ? 'white' : 'transparent'}
                    boxShadow={form.kind === k ? 'sm' : 'none'}
                    _hover={{ bg: form.kind === k ? 'white' : 'gray.200' }}
                    onClick={() => set('kind', k)}
                  >
                    {k === 'standard' ? 'Standard' : 'Referral'}
                  </Button>
                ))}
              </HStack>
              {form.kind === 'referral' && (
                <Box mt={2} p={3} bg="purple.50" rounded="md" fontSize="xs" color="purple.800">
                  <Text fontWeight="600" mb={1}>This is a referral template</Text>
                  Your app mints a unique link per user from it (each carries that user’s referrer ID).
                  Anyone who installs through a minted link is credited to that user on the referral
                  leaderboard. Below, just set <b>where referred users land</b>.
                </Box>
              )}
            </FormControl>

            {/* Link Name */}
            <FormControl isRequired isInvalid={!!field('linkName')}>
              <FormLabel>Link name</FormLabel>
              <Input
                placeholder="Promo - WhatsApp Blast"
                value={form.linkName}
                onChange={(e) => set('linkName', e.target.value)}
                maxLength={LINK_NAME_MAX}
                autoFocus
              />
              <FormErrorMessage>{field('linkName')}</FormErrorMessage>
              <FormHelperText>
                Helps you find this link later. Internal only. · {form.linkName.length}/{LINK_NAME_MAX}
              </FormHelperText>
            </FormControl>

            {/* Short-link preview */}
            <FormControl isInvalid={!!field('slug')}>
              <FormLabel>Short link</FormLabel>
              <HStack
                spacing={0}
                bg="gray.50"
                borderWidth="1px"
                rounded="md"
                px={3}
                py={2}
                align="center"
              >
                <Text fontFamily="mono" fontSize="sm" color="gray.600" wordBreak="break-all">
                  {previewUrl.replace(`/${form.slug}`, '/')}
                  <Text as="span" fontWeight="semibold" color="var(--brand-primary)">
                    {form.slug}
                  </Text>
                </Text>
                <Box flex={1} />
                <Tooltip label="Regenerate slug">
                  <IconButton
                    aria-label="Regenerate slug"
                    icon={<FiRefreshCw />}
                    size="sm"
                    variant="ghost"
                    onClick={regenerateSlug}
                  />
                </Tooltip>
              </HStack>
              <FormHelperText>
                {activeDomain
                  ? 'Auto-generated. Served on your custom domain:'
                  : 'Auto-generated. Your short link will be:'}{' '}
                <Text as="code" fontSize="xs">{previewUrl}</Text>
              </FormHelperText>
              <FormErrorMessage>{field('slug')}</FormErrorMessage>
            </FormControl>

            {/* Purpose */}
            <FormControl isRequired>
              <FormLabel>Purpose</FormLabel>
              <Select
                value={form.purpose}
                onChange={(e) => {
                  set('purpose', e.target.value as Purpose);
                  setErrors({});
                }}
              >
                {purposeOptions.map((p) => (
                  <option key={p} value={p}>
                    {PURPOSE_LABELS[p]}
                  </option>
                ))}
              </Select>
              <FormHelperText>
                {form.kind === 'referral'
                  ? 'Where referred users land. Referral attribution tracks app installs, so Website isn’t available.'
                  : 'What does this link do? Drives the fields below.'}
              </FormHelperText>
            </FormControl>

            <Divider />

            {/* Purpose-specific fields */}
            {form.purpose === 'website' && (
              <FormControl isRequired isInvalid={!!field('websiteUrl')}>
                <FormLabel>Website URL</FormLabel>
                <Input
                  placeholder="https://example.com/promo"
                  value={form.websiteUrl}
                  onChange={(e) => set('websiteUrl', e.target.value)}
                />
                <FormErrorMessage>{field('websiteUrl')}</FormErrorMessage>
              </FormControl>
            )}

            {form.purpose === 'app_download' && (
              <Box>
                <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={3}>
                  App download targets
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl isInvalid={!!field('androidAppStoreUrl')}>
                    <FormLabel>Android Play Store URL</FormLabel>
                    <Input
                      placeholder="https://play.google.com/store/apps/details?id=..."
                      value={form.androidAppStoreUrl}
                      onChange={(e) => set('androidAppStoreUrl', e.target.value)}
                    />
                    <FormErrorMessage>{field('androidAppStoreUrl')}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!field('iosAppStoreUrl')}>
                    <FormLabel>iOS App Store URL</FormLabel>
                    <Input
                      placeholder="https://apps.apple.com/app/id..."
                      value={form.iosAppStoreUrl}
                      onChange={(e) => set('iosAppStoreUrl', e.target.value)}
                    />
                    <FormErrorMessage>{field('iosAppStoreUrl')}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!field('ipadAppStoreUrl')}>
                    <FormLabel>iPad-only App Store URL (iOS)</FormLabel>
                    <Input
                      placeholder="https://apps.apple.com/app/id... (iPad build)"
                      value={form.ipadAppStoreUrl}
                      onChange={(e) => set('ipadAppStoreUrl', e.target.value)}
                    />
                    <FormHelperText>Optional. Used when an iPad is detected.</FormHelperText>
                    <FormErrorMessage>{field('ipadAppStoreUrl')}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!field('huaweiAppGalleryUrl')}>
                    <FormLabel>Huawei AppGallery URL</FormLabel>
                    <Input
                      placeholder="https://appgallery.huawei.com/app/..."
                      value={form.huaweiAppGalleryUrl}
                      onChange={(e) => set('huaweiAppGalleryUrl', e.target.value)}
                    />
                    <FormHelperText>Optional. Used on non-Google Huawei devices.</FormHelperText>
                    <FormErrorMessage>{field('huaweiAppGalleryUrl')}</FormErrorMessage>
                  </FormControl>
                  <FormControl isRequired isInvalid={!!field('webFallbackUrl')}>
                    <FormLabel>Fallback URL</FormLabel>
                    <Input
                      placeholder="https://example.com (used on desktop / other devices)"
                      value={form.webFallbackUrl}
                      onChange={(e) => set('webFallbackUrl', e.target.value)}
                    />
                    <FormErrorMessage>{field('webFallbackUrl')}</FormErrorMessage>
                  </FormControl>
                </VStack>
              </Box>
            )}

            {form.purpose === 'deep_link' && (
              <Box>
                <HStack justify="space-between" mb={3}>
                  <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Deep link
                  </Heading>
                  <DeepLinkPicker
                    onPick={(e: any) => {
                      if (e.android_link) set('androidDeepLink', e.android_link);
                      if (e.ios_link) set('iosDeepLink', e.ios_link);
                      if (e.web_fallback_url) set('webFallbackUrl', e.web_fallback_url);
                    }}
                  />
                </HStack>
                <VStack spacing={4} align="stretch">
                  <FormControl isInvalid={!!field('androidDeepLink')}>
                    <FormLabel>Android app deep link</FormLabel>
                    <Input
                      placeholder="https://links.example.com/product/123 (or exampleapp://...)"
                      value={form.androidDeepLink}
                      onChange={(e) => set('androidDeepLink', e.target.value)}
                    />
                    <FormErrorMessage>{field('androidDeepLink')}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!field('iosDeepLink')}>
                    <FormLabel>iOS deep link</FormLabel>
                    <Input
                      placeholder="https://links.example.com/product/123 (or exampleapp://...)"
                      value={form.iosDeepLink}
                      onChange={(e) => set('iosDeepLink', e.target.value)}
                    />
                    <FormErrorMessage>{field('iosDeepLink')}</FormErrorMessage>
                  </FormControl>
                </VStack>
                <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mt={5} mb={3}>
                  Fallback if app is not installed
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl isInvalid={!!field('androidAppStoreUrl')}>
                    <FormLabel>Android Play Store URL</FormLabel>
                    <Input
                      placeholder="https://play.google.com/store/apps/details?id=..."
                      value={form.androidAppStoreUrl}
                      onChange={(e) => set('androidAppStoreUrl', e.target.value)}
                    />
                    <FormErrorMessage>{field('androidAppStoreUrl')}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!field('iosAppStoreUrl')}>
                    <FormLabel>iOS App Store URL</FormLabel>
                    <Input
                      placeholder="https://apps.apple.com/app/id..."
                      value={form.iosAppStoreUrl}
                      onChange={(e) => set('iosAppStoreUrl', e.target.value)}
                    />
                    <FormErrorMessage>{field('iosAppStoreUrl')}</FormErrorMessage>
                  </FormControl>
                  {/* SM_006: web fallback for desktop / app-not-installed so a deep
                      link never dead-ends on a bare custom-scheme navigation. */}
                  <FormControl isInvalid={!!field('webFallbackUrl')}>
                    <FormLabel>Web fallback URL</FormLabel>
                    <Input
                      placeholder="https://example.com (opened on desktop or if the app isn't installed)"
                      value={form.webFallbackUrl}
                      onChange={(e) => set('webFallbackUrl', e.target.value)}
                    />
                    <FormHelperText>Recommended — where desktop visitors and users without the app land.</FormHelperText>
                    <FormErrorMessage>{field('webFallbackUrl')}</FormErrorMessage>
                  </FormControl>
                </VStack>
              </Box>
            )}

            {form.purpose === 'whatsapp' && (
              <Box>
                <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={3}>
                  WhatsApp
                </Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired isInvalid={!!field('whatsappNumber')}>
                    <FormLabel>WhatsApp number</FormLabel>
                    <Input
                      placeholder="923001234567 (country code, no + or spaces)"
                      value={form.whatsappNumber}
                      onChange={(e) => set('whatsappNumber', e.target.value)}
                    />
                    <FormErrorMessage>{field('whatsappNumber')}</FormErrorMessage>
                    <FormHelperText>Opens a WhatsApp chat with this number (wa.me). Include the country code.</FormHelperText>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Pre-filled message (optional)</FormLabel>
                    <Input
                      placeholder="Hi! I'd like to know more about…"
                      value={form.whatsappMessage}
                      onChange={(e) => set('whatsappMessage', e.target.value)}
                    />
                    <FormHelperText>Appears in the chat box, ready for them to send.</FormHelperText>
                  </FormControl>
                </VStack>
              </Box>
            )}

            {(form.purpose === 'app_download' || form.purpose === 'deep_link') && (
              <Box p={3} bg="purple.50" rounded="md" fontSize="xs" color="purple.800">
                <Text fontWeight="600" mb={1}>✓ Social-ready — from your App Profile</Text>
                Package, scheme and store URLs come from your <b>App Profile</b>. The store URLs below are
                <b> pre-filled from it</b> and used automatically (with social in-app-browser breakout). Edit
                any to override just this link.
              </Box>
            )}

            <Divider />

            {/* Tracking & lifecycle */}
            <Box>
              <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={3}>
                Tracking &amp; lifecycle
              </Heading>
              <VStack spacing={4} align="stretch">
                {form.kind === 'referral' ? (
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl>
                      <FormLabel>Referrer name</FormLabel>
                      <Input
                        value={form.campaignName}
                        onChange={(e) => set('campaignName', e.target.value)}
                        placeholder="John Doe"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Referral code</FormLabel>
                      <HStack spacing={2}>
                        <Input
                          value={form.referralCode}
                          onChange={(e) => set('referralCode', e.target.value)}
                          fontFamily="mono"
                          placeholder="A3FK9B"
                        />
                        <Tooltip label="Regenerate code">
                          <IconButton
                            aria-label="Regenerate referral code"
                            icon={<FiRefreshCw />}
                            variant="ghost"
                            onClick={() => set('referralCode', generateReferralCode())}
                          />
                        </Tooltip>
                      </HStack>
                      <FormHelperText>
                        Auto-generated — becomes the referrer’s ID + the utm{' '}
                        <Text as="code" fontSize="0.7rem">referrer_code</Text>.
                      </FormHelperText>
                    </FormControl>
                  </SimpleGrid>
                ) : (
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl>
                      <FormLabel>Campaign name</FormLabel>
                      <Input
                        value={form.campaignName}
                        onChange={(e) => set('campaignName', e.target.value)}
                        placeholder="Eid promo · app push"
                      />
                    </FormControl>
                    <FormControl isInvalid={!!field('campaignId')}>
                      <FormLabel>Campaign ID</FormLabel>
                      <Input
                        value={form.campaignId}
                        onChange={(e) => set('campaignId', e.target.value)}
                        placeholder="1042"
                      />
                      <FormErrorMessage>{field('campaignId')}</FormErrorMessage>
                      <FormHelperText>Letters, numbers, dot, hyphen, underscore. No spaces or symbols.</FormHelperText>
                    </FormControl>
                  </SimpleGrid>
                )}

                {/* SL_008: Contact Reference field removed from the Create form. */}

                <KeyValueEditor label="UTM parameters" value={utmParams} onChange={setUtmParams} onValidityChange={setUtmValid} />

                {form.purpose === 'deep_link' && (
                  <KeyValueEditor
                    label="Deep-link parameters"
                    value={deepLinkParams}
                    onChange={setDeepLinkParams}
                    onValidityChange={setParamsValid}
                  />
                )}

                <FormControl>
                  <FormLabel>Expires at</FormLabel>
                  <RadioGroup
                    value={form.expiryMode}
                    onChange={(v) => set('expiryMode', v as 'never' | 'on')}
                  >
                    <HStack spacing={6}>
                      <Radio value="never">Never expire</Radio>
                      <Radio value="on">Expires on</Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>

                {/* SM_012: the picker's min is "now" (local wall-clock) so a past
                    instant can't be chosen; submit validation still guards. */}
                {form.expiryMode === 'on' && (
                  <FormControl isInvalid={!!field('expiresAt')}>
                    <FormLabel>Expires at</FormLabel>
                    <Input
                      type="datetime-local"
                      min={toDatetimeLocalValue(new Date())}
                      value={form.expiresAt}
                      onChange={(e) => set('expiresAt', e.target.value)}
                    />
                    <FormErrorMessage>{field('expiresAt')}</FormErrorMessage>
                    <FormHelperText>Time is in {localTzLabel()}.</FormHelperText>
                  </FormControl>
                )}

                {/* SM_013: Attribution window is independent of expiry, so it stays
                    visible for both "Never expire" and "Expires on" — what the user
                    sees is always what gets saved. */}
                <FormControl isInvalid={!!field('attributionWindowHours')} maxW={{ sm: '260px' }}>
                  <FormLabel>Attribution window (h)</FormLabel>
                  <NumberInput
                    min={1}
                    max={2160}
                    value={form.attributionWindowHours}
                    onChange={(_s, n) => set('attributionWindowHours', Number.isNaN(n) ? 0 : n)}
                  >
                    <NumberInputField />
                  </NumberInput>
                  <FormErrorMessage>{field('attributionWindowHours')}</FormErrorMessage>
                  <FormHelperText>1–2160h (default 168 = 7 days). Independent of expiry.</FormHelperText>
                </FormControl>
              </VStack>
            </Box>

            {field('_root') && (
              <Text color="red.500" fontSize="sm">
                {field('_root')}
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" gap={3}>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isLoading} loadingText="Creating">
            Create link
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
